<template>
  <div
      class="transaction-items-card max-w-7xl bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mt-6 w-full max-w-full min-w-0"
      dir="rtl"
  >
    <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0 ">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
        <div class="min-w-0 overflow-hidden">
          <h3 class="text-sm font-bold text-gray-100 truncate">
            {{ title }}
          </h3>

          <p class="text-[11px] text-gray-400 mt-1 truncate">
            {{ subtitle }}
          </p>

          <div
              v-if="total > 0"
              class="grid grid-cols-2 sm:flex sm:flex-wrap items-stretch gap-2 mt-3 min-w-0"
          >
            <div class="rounded-xl bg-gray-900/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-gray-500 truncate">مجموع آیتم‌ها</p>
              <p class="text-xs font-bold text-gray-200 mt-1 truncate" dir="ltr">
                {{ formatNumber(total) }}
              </p>
            </div>

            <div class="rounded-xl bg-emerald-600/10 border border-emerald-500/20 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-emerald-300/80 truncate">فروش همین صفحه</p>
              <p class="text-xs font-bold text-emerald-400 mt-1 truncate" dir="ltr">
                {{ formatMoney(pageTotalAmount) }}
              </p>
            </div>
          </div>
        </div>

        <button
            @click="fetchData"
            :disabled="loading"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-200 hover:bg-gray-700 transition disabled:opacity-50 shrink-0"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          بروزرسانی
        </button>
      </div>
    </header>

    <!-- Filters -->
    <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
      <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 min-w-0">
        <!-- Source -->
        <div class="col-span-1 min-w-0">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            منبع فروش
          </label>

          <select
              v-model="selectedSource"
              @change="onFilterChange"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          >
            <option
                v-for="src in sourceOptions"
                :key="src.value"
                :value="src.value"
            >
              {{ src.label }}
            </option>
          </select>
        </div>

        <!-- Period -->
        <div class="col-span-1 min-w-0">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            بازه زمانی
          </label>

          <select
              v-model="selectedPeriod"
              @change="onPeriodChange"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          >
            <option
                v-for="option in periodOptions"
                :key="option.value"
                :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Start Date -->
        <div class="col-span-1 min-w-0" v-if="false">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            از تاریخ
          </label>

          <input
              type="date"
              v-model="customStartDate"
              @change="applyCustomDate"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <!-- End Date -->
        <div class="col-span-1 min-w-0" v-if="false">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            تا تاریخ
          </label>

          <input
              type="date"
              v-model="customEndDate"
              @change="applyCustomDate"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <!-- Page size -->
        <div class="col-span-2 lg:col-span-1 min-w-0">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            تعداد در صفحه
          </label>

          <select
              v-model.number="pageSize"
              @change="changePageSize"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          >
            <option :value="20">20 آیتم</option>
            <option :value="50">50 آیتم</option>
            <option :value="100">100 آیتم</option>
          </select>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 min-w-0">
        <div class="flex flex-wrap items-center gap-2 min-w-0 overflow-hidden">
          <span
              class="max-w-full min-w-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-[10px] sm:text-[11px] text-gray-300 overflow-hidden"
          >
            <i class="fas fa-filter text-gray-500 shrink-0"></i>
            <span class="truncate min-w-0">{{ activeFilterLabel }}</span>
          </span>

          <span
              v-if="partnerId"
              class="max-w-full min-w-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600/15 border border-indigo-500/30 text-[10px] sm:text-[11px] text-indigo-300 overflow-hidden"
          >
            <i class="fas fa-user-tag shrink-0"></i>
            <span class="truncate">partner_id: {{ partnerId }}</span>
          </span>
        </div>

        <button
            v-if="customStartDate || customEndDate || selectedPeriod === 'custom'"
            @click="clearCustomDate"
            class="w-full sm:w-auto px-3 py-1.5 rounded-xl bg-gray-800 border border-gray-600 text-[11px] text-gray-300 hover:bg-gray-700 transition shrink-0"
        >
          پاک کردن تاریخ دستی
        </button>
      </div>
    </section>

    <!-- Loading -->
    <div v-if="loading" class="p-10 text-center">
      <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
      <p class="text-gray-400 mt-3 text-sm">
        در حال دریافت اطلاعات...
      </p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="p-10 text-center">
      <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
      <p class="text-red-400 mt-3 text-sm">
        {{ error }}
      </p>

      <button
          @click="fetchData"
          class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-white text-xs transition"
      >
        تلاش مجدد
      </button>
    </div>

    <template v-else>
      <!-- Mobile Cards -->
      <div class="md:hidden p-3 space-y-3 w-full max-w-full min-w-0 overflow-hidden">
        <article
            v-for="item in normalizedItems"
            :key="item.rowKey"
            class="rounded-2xl bg-gray-900/45 border border-gray-700/80 p-3 shadow-sm w-full max-w-full min-w-0 overflow-hidden"
        >
          <!-- Row 1: Product + Source -->
          <div class="flex items-start gap-3 min-w-0 max-w-full overflow-hidden">
            <div class="w-14 h-14 rounded-2xl bg-gray-950 border border-gray-700 overflow-hidden flex-shrink-0">
              <img
                  v-if="item.productImage"
                  :src="item.productImage"
                  :alt="item.productName"
                  class="w-full h-full object-cover"
              />

              <div
                  v-else
                  class="w-full h-full flex items-center justify-center text-gray-500"
              >
                <i class="fas fa-image"></i>
              </div>
            </div>

            <div class="min-w-0 flex-1 overflow-hidden">
              <div class="flex items-start justify-between gap-2 min-w-0 overflow-hidden">
                <div class="min-w-0 flex-1 overflow-hidden">
                  <p class="text-xs font-bold text-gray-100 leading-5 line-clamp-2 break-safe">
                    {{ item.productName }}
                  </p>

                  <p class="text-[10px] text-gray-500 mt-1 truncate">
                    {{ item.productMeta }}
                  </p>
                </div>

                <span
                    :class="sourceBadgeClass(item.source)"
                    class="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border"
                >
                    <i :class="item.isReturn ? 'fas fa-undo-alt' : (item.source === 'shop' ? 'fas fa-globe' : 'fas fa-store')"></i>
                  {{ sourceLabel(item.source) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Row 2: Main numbers -->
          <div class="grid grid-cols-2 gap-2 mt-3 min-w-0">
            <div class="rounded-xl bg-gray-800/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-gray-500 truncate">قیمت کل</p>
              <p class="text-xs font-bold text-emerald-400 mt-1 truncate" dir="ltr">
                {{ formatMoney(item.lineTotal) }}
              </p>
            </div>

            <div class="rounded-xl bg-gray-800/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-gray-500 truncate">تعداد</p>
              <p class="text-xs font-bold text-gray-200 mt-1 truncate" dir="ltr">
                {{ formatNumber(item.quantity) }}
              </p>
            </div>
          </div>

          <!-- Row 3: Details compact -->
          <div class="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-[11px] min-w-0">
            <div class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">قیمت واحد</p>
              <p class="text-gray-300 mt-0.5 truncate" dir="ltr">
                {{ formatMoney(item.unitPrice) }}
              </p>
            </div>

            <div class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">تخفیف</p>
              <p
                  class="mt-0.5 truncate"
                  dir="ltr"
                  :class="item.discountAmount > 0 ? 'text-amber-400' : 'text-gray-500'"
              >
                {{ formatMoney(item.discountAmount) }}
              </p>
            </div>

            <div class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">مشتری</p>
              <p class="text-gray-300 mt-0.5 truncate" dir="ltr">
                {{ item.customer || '-' }}
              </p>
            </div>

            <div class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">سفارش</p>
              <p class="text-gray-300 mt-0.5 truncate" dir="ltr">
                #{{ item.orderId }}
              </p>
            </div>
          </div>

          <!-- Bottom -->
          <div class="mt-3 pt-3 border-t border-gray-700/70 flex items-center justify-between gap-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-gray-500 truncate min-w-0">
              {{ formatDate(item.orderDate) }}
            </p>

            <p
                v-if="item.trackingCode"
                class="text-[10px] text-gray-500 truncate min-w-0"
                dir="ltr"
            >
              {{ item.trackingCode }}
            </p>
          </div>
        </article>

        <div
            v-if="normalizedItems.length === 0"
            class="px-4 py-12 text-center rounded-2xl bg-gray-900/40 border border-gray-700 w-full max-w-full min-w-0 overflow-hidden"
        >
          <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
          <p class="text-gray-400 text-sm">
            داده‌ای برای نمایش وجود ندارد
          </p>
          <p class="text-gray-500 text-xs mt-1">
            فیلترها یا بازه زمانی را تغییر بده.
          </p>
        </div>
      </div>

      <!-- Desktop Table -->
      <div class="hidden md:block w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden table-scroll-safe">
        <table class="w-full min-w-[980px] text-sm">
          <thead class="bg-gray-900/70 border-b border-gray-700">
          <tr>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              نوع
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              محصول
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              تعداد
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              قیمت واحد
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              تخفیف
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              قیمت کل
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              تاریخ
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              سفارش
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">
              مشتری
            </th>
          </tr>
          </thead>

          <tbody class="divide-y divide-gray-700/50">
          <tr
              v-for="item in normalizedItems"
              :key="item.rowKey"
              class="hover:bg-gray-700/30 transition"
          >
            <td class="px-4 py-3 text-xs">
                <span
                    :class="sourceBadgeClass(item.source)"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
                >
                  <i :class="item.source === 'shop' ? 'fas fa-globe' : 'fas fa-store'"></i>
                  {{ sourceLabel(item.source) }}
                </span>
            </td>

            <td class="px-4 py-3">
              <div class="flex items-center gap-3 min-w-[260px] max-w-[320px] overflow-hidden">
                <div class="w-11 h-11 rounded-xl bg-gray-900 border border-gray-700 overflow-hidden flex-shrink-0">
                  <img
                      v-if="item.productImage"
                      :src="item.productImage"
                      :alt="item.productName"
                      class="w-full h-full object-cover"
                  />

                  <div
                      v-else
                      class="w-full h-full flex items-center justify-center text-gray-500"
                  >
                    <i class="fas fa-image"></i>
                  </div>
                </div>

                <div class="min-w-0 overflow-hidden">
                  <p class="text-xs text-gray-100 font-medium truncate">
                    {{ item.productName }}
                  </p>
                  <p class="text-[11px] text-gray-500 mt-1 truncate">
                    {{ item.productMeta }}
                  </p>
                </div>
              </div>
            </td>

            <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap" dir="ltr">
              {{ formatNumber(item.quantity) }}
            </td>

            <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap" dir="ltr">
              {{ formatMoney(item.unitPrice) }}
            </td>

            <td class="px-4 py-3 text-xs whitespace-nowrap" dir="ltr">
                <span :class="item.discountAmount > 0 ? 'text-amber-400' : 'text-gray-500'">
                  {{ formatMoney(item.discountAmount) }}
                </span>
            </td>

            <td
                class="px-4 py-3 text-xs font-bold whitespace-nowrap"
                :class="item.isReturn ? 'text-rose-400' : 'text-emerald-400'"
                dir="ltr"
            >
              {{ formatMoney(item.lineTotal) }}
            </td>

            <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
              {{ formatDate(item.orderDate) }}
            </td>

            <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap" dir="ltr">
              <div>#{{ item.orderId }}</div>
              <div v-if="item.trackingCode" class="text-[10px] text-gray-500 mt-1">
                {{ item.trackingCode }}
              </div>
            </td>

            <td class="px-4 py-3 text-xs text-gray-300 max-w-[160px]">
              <span class="block truncate" dir="ltr">{{ item.customer || '-' }}</span>
            </td>
          </tr>

          <tr v-if="normalizedItems.length === 0">
            <td colspan="9" class="px-4 py-12 text-center">
              <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
              <p class="text-gray-400 text-sm">
                داده‌ای برای نمایش وجود ندارد
              </p>
              <p class="text-gray-500 text-xs mt-1">
                فیلترها یا بازه زمانی را تغییر بده.
              </p>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Pagination -->
    <footer
        v-if="!loading && !error && total > 0"
        class="px-3 sm:px-4 py-4 bg-gray-900/30 border-t border-gray-700 min-w-0 overflow-hidden"
    >
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
        <div class="text-[11px] sm:text-xs text-gray-400 text-center sm:text-right min-w-0">
          نمایش
          <b class="text-gray-200">{{ formatNumber(fromItem) }}</b>
          تا
          <b class="text-gray-200">{{ formatNumber(toItem) }}</b>
          از
          <b class="text-gray-200">{{ formatNumber(total) }}</b>
          آیتم
        </div>

        <div class="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-2 min-w-0">
          <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1 || loading"
              class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
              :class="currentPage > 1 ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
          >
            قبلی
          </button>

          <span class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-700 text-xs text-gray-300 text-center truncate">
            {{ formatNumber(currentPage) }} / {{ formatNumber(totalPages) }}
          </span>

          <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage >= totalPages || loading"
              class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
              :class="currentPage < totalPages ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
          >
            بعدی
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'TransactionItemsList',

  props: {
    title: {
      type: String,
      default: 'لیست آیتم‌های فروش'
    },
    subtitle: {
      type: String,
      default: 'نمایش آیتم‌های فروش آنلاین و حضوری بر اساس بازه زمانی'
    },
    partnerId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      loading: false,
      error: null,

      selectedPeriod: 'today',
      selectedSource: 'pos',

      customStartDate: '',
      customEndDate: '',

      periodOptions: [
        { value: 'today', label: 'امروز' },
        { value: 'yesterday', label: 'دیروز' },
        { value: 'this_week', label: 'این هفته' },
        { value: 'last_week', label: 'هفته قبل' },
        { value: 'this_month', label: 'این ماه' },
        { value: 'last_month', label: 'ماه قبل' },
        { value: 'custom', label: 'بازه دستی' }
      ],

      sourceOptions: [
        { value: 'both', label: 'همه منابع' },
        { value: 'shop', label: 'فروش آنلاین' },
        { value: 'pos', label: 'فروش حضوری' }
      ],

      items: [],

      currentPage: 1,
      totalPages: 1,
      total: 0,
      pageSize: 20
    }
  },

  computed: {
    normalizedItems() {
      return (this.items || []).map((item) => {
        const product = item.product || {}

        const isReturn = Boolean(
            item.is_return ||
            item.row_type === 'return' ||
            item.source === 'pos_return' ||
            item.source === 'shop_return'
        )
        const rawQuantity = Number(item.quantity || 0)
        const rawLineTotal = Number(item.line_total || 0)
        return {
          rowKey: `${item.source}-${item.item_id}`,
          id: item.item_id,
          source: item.source,
          isReturn,
          orderId: item.order_id,
          orderStatus: item.order_status,
          orderDate: item.order_date,
          trackingCode: item.tracking_code,

          customer: item.customer,

          productId: product.id,
          productName: product.name || '-',
          productImage: product.image || null,
          productCode: product.sixteen_digit_code || null,
          variationName: product.variation_of_name || null,
          productType: product.product_type || null,
          productMeta: this.buildProductMeta(product),

          quantity: isReturn && rawQuantity > 0 ? -rawQuantity : rawQuantity,
          unitPrice: item.unit_price || 0,
          lineSubtotal: item.line_subtotal || 0,
          discountAmount: item.line_discount_amount || 0,
          lineTotal: isReturn && rawLineTotal > 0 ? -rawLineTotal : rawLineTotal,

          partnerId: item.partner_id,
          partnerName: item.partner_name
        }
      })
    },

    fromItem() {
      if (this.total === 0) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },

    toItem() {
      return Math.min(this.currentPage * this.pageSize, this.total)
    },

    pageTotalAmount() {
      return this.normalizedItems.reduce((sum, item) => {
        return sum + Number(item.lineTotal || 0)
      }, 0)
    },

    activeFilterLabel() {
      const source = this.sourceOptions.find(item => item.value === this.selectedSource)
      const period = this.periodOptions.find(item => item.value === this.selectedPeriod)

      const sourceLabel = source ? source.label : 'همه منابع'
      const periodLabel = period ? period.label : 'امروز'

      if (this.selectedPeriod === 'custom') {
        const start = this.customStartDate || 'ابتدا'
        const end = this.customEndDate || 'انتها'
        return `${sourceLabel} / ${start} تا ${end}`
      }

      return `${sourceLabel} / ${periodLabel}`
    }
  },

  watch: {
    partnerId() {
      this.currentPage = 1
      this.fetchData()
    }
  },

  methods: {
    buildProductMeta(product) {
      const parts = []

      if (product.sixteen_digit_code) {
        parts.push(product.sixteen_digit_code)
      }

      if (product.variation_of_name) {
        parts.push(product.variation_of_name)
      }

      if (product.product_type) {
        parts.push(product.product_type)
      }

      return parts.length ? parts.join(' / ') : 'بدون کد'
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'

      const number = Number(value)
      if (Number.isNaN(number)) return '۰'

      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    formatDate(dateString) {
      if (!dateString) return '-'

      const date = new Date(dateString)

      if (Number.isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },

    formatDateForApi(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    getDateParams() {
      if (this.selectedPeriod === 'custom') {
        return {
          start_date: this.customStartDate || '',
          end_date: this.customEndDate || ''
        }
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      const startOfWeek = new Date(today)
      const jsDay = today.getDay()
      const daysSinceSaturday = jsDay === 6 ? 0 : jsDay + 1
      startOfWeek.setDate(today.getDate() - daysSinceSaturday)

      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 6)

      const startOfLastWeek = new Date(startOfWeek)
      startOfLastWeek.setDate(startOfWeek.getDate() - 7)

      const endOfLastWeek = new Date(endOfWeek)
      endOfLastWeek.setDate(endOfWeek.getDate() - 7)

      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)

      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)

      switch (this.selectedPeriod) {
        case 'today':
          return {
            start_date: this.formatDateForApi(today),
            end_date: this.formatDateForApi(today)
          }

        case 'yesterday':
          return {
            start_date: this.formatDateForApi(yesterday),
            end_date: this.formatDateForApi(yesterday)
          }

        case 'this_week':
          return {
            start_date: this.formatDateForApi(startOfWeek),
            end_date: this.formatDateForApi(endOfWeek)
          }

        case 'last_week':
          return {
            start_date: this.formatDateForApi(startOfLastWeek),
            end_date: this.formatDateForApi(endOfLastWeek)
          }

        case 'this_month':
          return {
            start_date: this.formatDateForApi(startOfMonth),
            end_date: this.formatDateForApi(endOfMonth)
          }

        case 'last_month':
          return {
            start_date: this.formatDateForApi(startOfLastMonth),
            end_date: this.formatDateForApi(endOfLastMonth)
          }

        default:
          return {}
      }
    },

    sourceLabel(source) {
      if (source === 'shop') return 'آنلاین'
      if (source === 'pos') return 'حضوری'
      if (source === 'pos_return') return 'مرجوعی حضوری'
      if (source === 'shop_return') return 'مرجوعی آنلاین'
      return 'نامشخص'
    },

    sourceBadgeClass(source) {
      if (source === 'shop') {
        return 'bg-blue-600/15 text-blue-300 border-blue-500/30'
      }

      if (source === 'pos') {
        return 'bg-purple-600/15 text-purple-300 border-purple-500/30'
      }

      if (source === 'pos_return' || source === 'shop_return') {
        return 'bg-rose-600/15 text-rose-300 border-rose-500/30'
      }

      return 'bg-gray-600/15 text-gray-300 border-gray-500/30'
    },
    buildEndpoint(path) {
      if (this.endpoint && typeof this.endpoint === 'function') {
        return this.endpoint(path)
      }

      return path
    },

    fetchData() {
      this.loading = true
      this.error = null

      let url = this.buildEndpoint('deliver/chart/items')

      const params = []

      params.push(`source=${encodeURIComponent(this.selectedSource)}`)

      if (this.partnerId) {
        params.push(`partner_id=${encodeURIComponent(this.partnerId)}`)
      }

      const dateParams = this.getDateParams()

      if (dateParams.start_date) {
        params.push(`start_date=${encodeURIComponent(dateParams.start_date)}`)
      }

      if (dateParams.end_date) {
        params.push(`end_date=${encodeURIComponent(dateParams.end_date)}`)
      }

      params.push(`page=${encodeURIComponent(this.currentPage)}`)
      params.push(`page_size=${encodeURIComponent(this.pageSize)}`)

      url += `?${params.join('&')}`

      this.request({
        url,
        method: 'get',
        success: (response) => {
          this.processData(response)
        },
        error: (err) => {
          console.error('TransactionItemsList API error:', err)
          this.error = 'خطا در دریافت اطلاعات'
          this.loading = false
        }
      })
    },

    processData(data) {
      try {
        const results = Array.isArray(data.results) ? data.results : []

        this.items = results
        this.total = Number(data.count || 0)
        this.totalPages = Math.max(1, Math.ceil(this.total / this.pageSize))

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages
        }

        this.loading = false
      } catch (err) {
        console.error('TransactionItemsList process error:', err)
        this.error = 'خطا در پردازش اطلاعات'
        this.loading = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.fetchData()
    },

    onPeriodChange() {
      if (this.selectedPeriod !== 'custom') {
        this.customStartDate = ''
        this.customEndDate = ''
      }

      this.currentPage = 1
      this.fetchData()
    },

    applyCustomDate() {
      if (this.customStartDate || this.customEndDate) {
        this.selectedPeriod = 'custom'
        this.currentPage = 1
        this.fetchData()
      }
    },

    clearCustomDate() {
      this.customStartDate = ''
      this.customEndDate = ''
      this.selectedPeriod = 'today'
      this.currentPage = 1
      this.fetchData()
    },

    goToPage(page) {
      if (page < 1) return
      if (page > this.totalPages) return
      if (page === this.currentPage) return

      this.currentPage = page
      this.fetchData()
    },

    changePageSize() {
      this.currentPage = 1
      this.fetchData()
    }
  },

  mounted() {
    this.fetchData()
  }
}
</script>

<style scoped>
.transaction-items-card,
.transaction-items-card * {
  box-sizing: border-box;
}

.transaction-items-card {
  contain: layout paint;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.break-safe {
  overflow-wrap: anywhere;
  word-break: break-word;
}

input[type="date"],
select,
button {
  max-width: 100%;
  min-width: 0;
}

img {
  max-width: 100%;
}

table {
  border-collapse: collapse;
}

.table-scroll-safe {
  scrollbar-width: thin;
}

/* جلوگیری از overflow بدنه در موبایل به خاطر عددهای طولانی */
.truncate {
  min-width: 0;
}

/* مهم برای flex/grid های تو در تو */
.min-w-0 {
  min-width: 0 !important;
}

@media (max-width: 767px) {
  .transaction-items-card {
    width: 90%;
    max-width: 95%;
    overflow-x: hidden;
  }
}
</style>